import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { useUsers } from "../../../../components/Common/HelperComponents/GetDataFromStore";
import { getAudit, listAuditEvents, unwrapGet } from "../../../../helpers/auditHub_helper";
import { getComplianceIcon } from "../../../../assets/images/compliance/IconCompliance.jsx";
import ActivityTimeline from "./ActivityTimeline";
import { AH_ACCENT, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, auditHubPageSx } from "../theme";
import { formatShortDate, formatUserName, frameworkLabel } from "../utils";

const ActivityTrailDialog = ({ audit, open, onClose }) => {
  const { users } = useUsers();

  const userNames = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((user) => {
      if (user?.email) map.set(String(user.email).toLowerCase(), formatUserName(user));
    });
    return map;
  }, [users]);

  const nameFor = useCallback(
    (email) => userNames.get(String(email || "").toLowerCase()) || email || "",
    [userNames]
  );

  const auditId = audit?.auditId;
  const fetchPage = useCallback((params) => listAuditEvents(auditId, params), [auditId]);
  const auditCode = audit?.auditCode;
  const auditName = audit?.name;
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !auditId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetail(null);
    getAudit(auditId)
      .then((res) => {
        if (!cancelled) setDetail(unwrapGet(res));
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, auditId]);

  const frameworkNames = useMemo(() => {
    const names = [];
    const seen = new Set();
    (detail?.accounts || []).forEach((account) => {
      (account.scopes || []).forEach((scope) => {
        const label = frameworkLabel(scope?.framework);
        if (!label || label === "—" || seen.has(label)) return;
        seen.add(label);
        names.push(label);
      });
    });
    return names;
  }, [detail]);

  const dueDate = detail?.dueDate ?? audit?.dueDate;
  const summaryParts = [
    auditCode ? { key: "id", label: "ID", value: auditCode } : null,
    auditName ? { key: "name", label: "Assessment", value: auditName } : null,
    dueDate ? { key: "due", label: "Target date", value: formatShortDate(dueDate) } : null,
    frameworkNames.length ? { key: "framework", label: "Framework", frameworks: frameworkNames } : null,
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="audit-activity-trail-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 720 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
          flexShrink: 0,
        }}
      >
        <Typography id="audit-activity-trail-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Activity Trail
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        {summaryParts.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 1.5,
              mb: 2.5,
              px: 1.75,
              py: 1.5,
              borderRadius: "10px",
              backgroundColor: AH_TRACK,
              border: `1px solid ${AH_BORDER}`,
            }}
          >
            {summaryParts.map((part) => (
              <Box key={part.key} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: AH_MUTED,
                    lineHeight: 1.2,
                  }}
                >
                  {part.label}
                </Typography>
                {part.frameworks ? (
                  <Box sx={{ mt: 0.35, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    {part.frameworks.map((name) => {
                      const icon = getComplianceIcon(name);
                      return (
                        <Box key={name} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                          {icon ? (
                            <Box
                              component="img"
                              src={icon}
                              alt=""
                              sx={{ height: 18, width: "auto", maxWidth: 24, objectFit: "contain", flexShrink: 0 }}
                            />
                          ) : null}
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: AH_TEXT, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                            {name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: AH_TEXT,
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {part.value}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null}
        {open && auditId ? (
          <ActivityTimeline
            fetchPage={fetchPage}
            resetKey={auditId}
            nameFor={nameFor}
            showControl
            emptyText="No activity recorded for this assessment yet."
          />
        ) : null}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            minWidth: 140,
            height: 38,
            px: 2,
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 400,
            fontSize: "0.875rem",
            boxShadow: "none",
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default ActivityTrailDialog;
